import { STRIPE_JS_SRC } from '@/lib/constants/stripe';
import type { Stripe, StripeConstructor } from './stripeTypes';

/**
 * Runtime loader for Stripe.js. Injects `<script src="https://js.stripe.com/v3/">`
 * once, on first use (only card-entry surfaces call it), and hands back `Stripe`
 * instances cached per publishable key. A failed load clears the cached promise so
 * a user-triggered retry re-injects the tag (ad blockers and captive networks are
 * the usual cause). No npm package: the official loader is only this plus types
 * (ADR-010; notes/billing-card-entry.md D1).
 */
export class StripeJs {
  private static loadPromise: Promise<StripeConstructor> | null = null;
  private static readonly instances = new Map<string, Stripe>();

  static load(): Promise<StripeConstructor> {
    if (window.Stripe) return Promise.resolve(window.Stripe);
    if (this.loadPromise) return this.loadPromise;

    this.loadPromise = new Promise<StripeConstructor>((resolve, reject) => {
      const fail = (reason: string) => {
        this.loadPromise = null;
        reject(new Error(reason));
      };

      const existing = document.querySelector<HTMLScriptElement>(`script[src="${STRIPE_JS_SRC}"]`);
      const script = existing ?? document.createElement('script');
      script.addEventListener('load', () => {
        if (window.Stripe) resolve(window.Stripe);
        else fail('Stripe.js loaded but did not initialize.');
      });
      script.addEventListener('error', () => {
        script.remove();
        fail('The secure card form could not be loaded. A browser extension or network filter may be blocking it.');
      });
      if (!existing) {
        script.src = STRIPE_JS_SRC;
        script.async = true;
        document.head.appendChild(script);
      }
    });
    return this.loadPromise;
  }

  /** `Stripe(publishableKey)` — one instance per key for the page lifetime. */
  static async create(publishableKey: string): Promise<Stripe> {
    const cached = this.instances.get(publishableKey);
    if (cached) return cached;
    const ctor = await this.load();
    const instance = ctor(publishableKey);
    this.instances.set(publishableKey, instance);
    return instance;
  }
}
